import {isNil} from 'lodash';
import {useEffect, useState, useContext} from 'react';
import useFileUpload from '../../../../../../shared/hooks/use-file-upload.hook';
import {Check, CheckType} from '../../../../../../shared/model/check.model';
import {FieldType} from '../../../../../../shared/model/field.model';
import {NxFile} from '../../../../../../shared/model/nx-file.model';
import {Phase} from '../../../../../../shared/model/phase.model';
import {NumberHelper} from '../../../../../../utils/number-helper';
import {AppSnackbarContext} from '../../../../../shared/app-snackbar-provider/AppSnackbarProvider';
import {FALSE_STRING} from '../../../../../shared/form-bolean-option-values/YesNoSelectOptions';
import {CheckValue, CheckValueTO} from './check-value.model';
import {handleNxFiles, parseSavedCheck} from './check-values-utils';

const getInitValues = (checks: Check[], valuesTO?: CheckValueTO[]): CheckValue[] =>
  checks.map(check => {

    
    const {automatic, id, proofType, type, required} = check;
    const savedCheck = valuesTO?.find(({checkId}) => checkId === id);
    const savedValue = savedCheck?.value;

    let value = isNil(savedValue) && type !== CheckType.MANUAL
      ? FALSE_STRING
      : parseSavedCheck(savedValue, proofType);
    value = handleNumericProofType(value, proofType);

    return {id: savedCheck?.id, checkId: id, isAutomatic: automatic, required, value, fieldType: proofType, lastModifiedByUserId: savedCheck?.lastModifiedByUserId};
  });

function handleNumericProofType<T>(value: T, proofType: FieldType | undefined): T | number | undefined{
  switch (proofType) {
    case FieldType.CASH:
    case FieldType.INTEGER:
    case FieldType.FLOAT:
    case FieldType.PERCENTAGE:
      return NumberHelper.toNumberOrUndefined(value);
    default:
      return value;
  }
}

export default function useCustomCheckInitValues(config?: Phase,
                                                 valuesTO?: CheckValueTO[]): (CheckValue | CheckValue<number> | CheckValue<NxFile>)[] | undefined {

  const {downloadFile} = useFileUpload();
  const [checkInitValues, setCheckInitValues] = useState<(CheckValue | CheckValue<NxFile>)[]>();
  const {showErrorMessage} = useContext(AppSnackbarContext);

  useEffect(() => {
    if (isNil(config)) {
      return;
    }

    // Flag to prevent state updates if the component unmounts or the effect re-runs before promises resolve
    let isCancelled = false;

    const checks = config.checkGroups.flatMap(checkGroup => checkGroup.checks);
    const promises = getInitValues(checks, valuesTO)
      .map(value => handleNxFiles(value, downloadFile));

    Promise.all(promises)
      .then(resolvedData => {
        if (!isCancelled) {
          setCheckInitValues(resolvedData as (CheckValue | CheckValue<NxFile>)[]);
        }
      })
      .catch(e => {
        if (!isCancelled) {
          showErrorMessage(`Error when handling check files: ${e.message}`)
        }
    });

    return () => {
      isCancelled = true;
    };
  }, [config, valuesTO]);

  return checkInitValues;
}
